import { useState, useEffect } from 'react';
import { X, Plus, Minus, ShoppingBag, Truck, Shield, Clock, Star, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useCart } from '@/contexts/CartContext';
import { useNavigate, Link } from 'react-router-dom';
import { PriceDisplay } from './PriceDisplay';
import { useShippingSettings } from '@/hooks/useShippingSettings';
import { useLanguage } from '@/contexts/LanguageContext';

const Cart = ({ mobileNav = false }: { mobileNav?: boolean }) => {
  const { items, removeFromCart, updateQuantity, getTotalPrice, getTotalItems, loading, hasConsultationItems, hasUnapprovedConsultationItems, isConsultationApproved, forceRefreshCart, manualClearCart, isOrderCompleting } = useCart();
  const { config: shippingConfig, loading: shippingLoading } = useShippingSettings();
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnapprovedItems, setHasUnapprovedItems] = useState(false);
  const [approvedItems, setApprovedItems] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Handle browser back button to close cart
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isOpen) {
        event.preventDefault();
        setIsOpen(false);
        // Push the state back to stay on the same page
        window.history.pushState(null, '', window.location.href);
      }
    };

    if (isOpen) {
      window.history.pushState({ cartOpen: true }, '');
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen]);

  console.log('Cart component render - items:', items.length, 'loading:', loading, 'shippingLoading:', shippingLoading);

  const subtotal = getTotalPrice();
  const isEligibleForFreeShipping = subtotal >= shippingConfig.free_shipping_threshold;
  const shippingCost = isEligibleForFreeShipping ? 0 : shippingConfig.standard_shipping_cost;
  const totalWithShipping = subtotal + shippingCost;
  const hasConsultationProducts = hasConsultationItems();
  // Calculate visible items and their total
  const visiblePriceItems = items.filter(item => 
    !(item.requires_consultation && item.hide_price_without_consultation && !approvedItems.has(item.id))
  );
  const hasAnyHiddenPriceItems = items.some(item => 
    item.requires_consultation && item.hide_price_without_consultation && !approvedItems.has(item.id)
  );
  const visibleSubtotal = visiblePriceItems.reduce((total, item) => {
    const itemPrice = item.discount_percentage && item.discount_percentage > 0 
      ? item.price * (1 - item.discount_percentage / 100) 
      : item.price;
    return total + (itemPrice * item.quantity);
  }, 0);
  const visibleIsEligibleForFreeShipping = visibleSubtotal >= shippingConfig.free_shipping_threshold;
  const visibleShippingCost = visibleIsEligibleForFreeShipping ? 0 : shippingConfig.standard_shipping_cost;
  const visibleTotalWithShipping = visibleSubtotal + visibleShippingCost;
  
  const handleCheckout = () => {
    setIsOpen(false);
    navigate('/checkout');
  };

  // Check for unapproved consultation items and track approved ones
  useEffect(() => {
    const checkApprovals = async () => {
      const hasUnapproved = await hasUnapprovedConsultationItems();
      setHasUnapprovedItems(hasUnapproved);
      
      // Check which items are approved
      const approved = new Set<string>();
      for (const item of items) {
        if (item.requires_consultation) {
          const isApproved = await isConsultationApproved(item.id);
          if (isApproved) {
            approved.add(item.id);
          }
        }
      }
      setApprovedItems(approved);
    };
    
    if (hasConsultationProducts) {
      checkApprovals();
    } else {
      setHasUnapprovedItems(false);
      setApprovedItems(new Set());
    }
  }, [items, hasConsultationProducts]);

  // Check consultation status every time cart is opened
  useEffect(() => {
    const checkApprovals = async () => {
      if (hasConsultationProducts) {
        const hasUnapproved = await hasUnapprovedConsultationItems();
        setHasUnapprovedItems(hasUnapproved);
        
        // Update approved items
        const approved = new Set<string>();
        for (const item of items) {
          if (item.requires_consultation) {
            const isApproved = await isConsultationApproved(item.id);
            if (isApproved) {
              approved.add(item.id);
            }
          }
        }
        setApprovedItems(approved);
      }
    };
    
    if (isOpen) {
      checkApprovals();
    }
  }, [isOpen, hasConsultationProducts]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={mobileNav ? "flex-col h-auto py-2 px-6 gap-1.5 relative hover:bg-accent/60 active:scale-95 transition-all duration-200 rounded-xl min-w-[72px]" : "relative"} 
          data-testid="cart-trigger"
          aria-label={t.cart.title}
        >
          <div className="relative">
            <ShoppingBag size={mobileNav ? 22 : 20} className="text-foreground/80" strokeWidth={2} />
            {getTotalItems() > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center border-2 border-background">
                {getTotalItems()}
              </span>
            )}
          </div>
          {mobileNav && <span className="text-[10px] font-medium text-foreground/70">{t.cart.title}</span>}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="border-b pb-4">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <ShoppingBag size={24} className="text-primary" />
            {t.cart.title}
            <Badge variant="secondary" className="ml-auto">
              {getTotalItems()} {getTotalItems() === 1 ? t.cart.item : t.cart.items}
            </Badge>
          </SheetTitle>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 overflow-hidden">
          {(items.length === 0 && !isOrderCompleting && !loading) ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto bg-muted rounded-full flex items-center justify-center">
                  <ShoppingBag size={32} className="text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t.cart.empty}</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {t.cart.emptyDescription}
                  </p>
                  <Button onClick={() => setIsOpen(false)} variant="outline">
                    {t.cart.continueShopping}
                  </Button>
                </div>
              </div>
            </div>
          ) : loading || isOrderCompleting ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">
                    {isOrderCompleting ? t.cart.preparingOrder : t.cart.loading}
                  </h3>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {/* Trust indicators */}
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex flex-col gap-2 text-xs">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Truck size={14} />
                      <span className="flex-1">
                        {visibleIsEligibleForFreeShipping ? t.cart.freeDelivery : (hasAnyHiddenPriceItems ? t.cart.delivery : `${t.cart.delivery} €${shippingConfig.standard_shipping_cost.toFixed(2)}`)}
                      </span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Shield size={14} />
                    <span className="flex-1">{t.cart.securePayment}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Star size={14} />
                    <span className="flex-1">{t.cart.qualityGuarantee}</span>
                  </div>
                </div>
              </div>

              {/* Products list */}
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={item.id} className="bg-card rounded-lg border p-3 shadow-sm">
                    <div className="flex gap-3">
                      <div className="relative flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg border"
                        />
                        <div className="absolute -top-1 -left-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                          {index + 1}
                        </div>
                        {item.requires_consultation && !approvedItems.has(item.id) && (
                          <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            !
                          </div>
                        )}
                        {item.requires_consultation && approvedItems.has(item.id) && (
                          <div className="absolute -top-1 -right-1 bg-emerald-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            <CheckCircle2 size={12} />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                         <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0 pr-2">
                            <Link
                              to={`/produktas/${item.id}`}
                              className="font-semibold text-sm leading-tight hover:text-primary transition-colors break-words"
                              title={item.name}
                              onClick={() => setIsOpen(false)}
                            >
                              {item.name}
                            </Link>
                            <p className="text-xs text-muted-foreground mt-1 break-words" title={item.brand}>
                              {item.brand}
                            </p>
                            {item.requires_consultation && !approvedItems.has(item.id) && (
                              <Badge className="bg-orange-500 text-white text-xs mt-1">
                                {t.cart.consultationRequired}
                              </Badge>
                            )}
                            {item.requires_consultation && approvedItems.has(item.id) && (
                              <Badge className="bg-emerald-500 text-white text-xs mt-1 flex items-center gap-1">
                                <CheckCircle2 size={10} />
                                {t.cart.consultationProvided}
                              </Badge>
                            )}
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive flex-shrink-0"
                            onClick={() => removeFromCart(item.id)}
                            disabled={loading}
                          >
                            <X size={12} />
                          </Button>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-7 w-7"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1 || loading}
                            >
                              <Minus size={12} />
                            </Button>
                            
                            <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                            
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-7 w-7"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              disabled={loading}
                            >
                              <Plus size={12} />
                            </Button>
                          </div>
                          
                          <div className="text-right flex-shrink-0">
                            <PriceDisplay 
                              price={item.price * item.quantity}
                              discountPercentage={item.discount_percentage}
                              className="text-sm font-bold"
                              showLoginPrompt={false}
                              hidePriceWithoutConsultation={item.hide_price_without_consultation}
                              requiresConsultation={item.requires_consultation}
                              hasConsultationApproval={approvedItems.has(item.id)}
                            />
                            {item.quantity > 1 && !(item.requires_consultation && item.hide_price_without_consultation && !approvedItems.has(item.id)) && (
                              <p className="text-xs text-muted-foreground">
                                €{(item.discount_percentage && item.discount_percentage > 0 
                                  ? item.price * (1 - item.discount_percentage / 100) 
                                  : item.price).toFixed(2)} {t.cart.each}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Consultation warning */}
              {hasUnapprovedItems && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-start gap-2 text-orange-800">
                    <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium mb-1">{t.cart.consultationWarningTitle}</p>
                      <p className="text-xs">
                        {t.cart.consultationWarningDesc}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Delivery estimate */}
              <div className="bg-sage/10 border border-sage/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sage-foreground">
                  <Clock size={16} />
                  <span className="text-sm font-medium">
                    {language === 'en' && shippingConfig.delivery_time_text_en 
                      ? shippingConfig.delivery_time_text_en 
                      : shippingConfig.delivery_time_text}
                  </span>
                </div>
              </div>

              {/* Summary section */}
              <div className="border-t pt-4 space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>{t.cart.itemsValue}</span>
                    {hasAnyHiddenPriceItems ? (
                      <span className="font-medium text-muted-foreground">—</span>
                    ) : (
                      <span className="font-medium">€{visibleSubtotal.toFixed(2)}</span>
                    )}
                  </div>
                   <div className="flex justify-between text-sm">
                     <span>{t.cart.deliveryLabel}</span>
                     {hasAnyHiddenPriceItems ? (
                       <span className="font-medium text-muted-foreground">—</span>
                     ) : (
                       <span className={`font-medium ${visibleIsEligibleForFreeShipping ? 'text-sage-foreground' : ''}`}>
                         {visibleIsEligibleForFreeShipping ? t.cart.free : `€${visibleShippingCost.toFixed(2)}`}
                       </span>
                     )}
                   </div>
                  <Separator />
                   <div className="flex justify-between items-center">
                     <span className="text-lg font-bold">{t.cart.total}</span>
                     {hasAnyHiddenPriceItems ? (
                       <span className="text-xl font-bold text-muted-foreground">—</span>
                     ) : (
                       <span className="text-xl font-bold text-primary">€{visibleTotalWithShipping.toFixed(2)}</span>
                     )}
                   </div>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-2">
                  <Button 
                    className="w-full btn-hero text-base font-semibold py-3"
                    onClick={handleCheckout}
                    disabled={hasUnapprovedItems}
                  >
                    <ShoppingBag size={18} className="mr-2" />
                    {hasUnapprovedItems ? t.cart.paymentUnavailable : t.cart.buyNow}
                  </Button>
                  
                  {hasUnapprovedItems && (
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/contact?type=consultation');
                      }}
                    >
                      <AlertTriangle size={18} className="mr-2" />
                      {t.cart.contactForConsultation}
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setIsOpen(false)}
                  >
                    {t.cart.continueShopping}
                  </Button>
                </div>

                {/* Security badge */}
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
                  <Shield size={12} />
                  <span>{t.cart.sslProtection}</span>
                </div>
                
                {/* Debug buttons - temporary */}
                {items.length > 0 && (
                  <div className="flex gap-2 pt-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 text-xs text-muted-foreground"
                      onClick={forceRefreshCart}
                      disabled={loading}
                    >
                      {t.cart.updateCart}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 text-xs text-muted-foreground"
                      onClick={manualClearCart}
                      disabled={loading}
                    >
                      {t.cart.clearCart}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Cart;